<template>
  <div class="isolate mm-root t24-tools mm-clips clips-template-page">
    <section class="clips-template-shell">
      <header class="clips-template-header">
        <NuxtLink class="clips-template-back" to="/clips">{{ t('clips.title') }}</NuxtLink>
        <h1>{{ t('clips.templates.title') }}</h1>
        <p>{{ t('clips.templates.subtitle') }}</p>
      </header>

      <p v-if="isLoading" role="status">{{ t('common.loading') }}</p>
      <div v-else-if="errorMessage" role="alert" class="clips-template-feedback">
        <p>{{ errorMessage }}</p>
        <button type="button" class="btn btn-secondary" @click="loadTemplates">{{ t('ux.retry') }}</button>
      </div>
      <p v-else-if="!templates.length" role="status">{{ t('clips.templates.empty') }}</p>
      <p v-if="remixError" role="alert" class="clips-template-feedback">{{ remixError }}</p>

      <section class="clips-template-grid">
        <article v-for="template in templates" :key="template.id" class="clips-template-card">
          <span class="tile-ic" aria-hidden="true"><span class="i-lucide-clapperboard" /></span><div class="template-copy">
          <h2>{{ template.title }}</h2>
          <p>{{ template.description }}</p>
          </div><p class="clips-template-category">{{ template.category }}</p><ClipsTemplatePreview :brief="template.remix_request.script_or_brief" />
          <button class="btn btn-secondary clips-template-button" type="button" :disabled="isPreparing" @click="startTemplateRemix(template)">
            <span class="i-lucide-wand-sparkles" aria-hidden="true"></span>
            {{ t('clips.public.makeYours') }}
          </button>
        </article>
      </section>
    </section>
  </div>
</template>

<script setup lang="ts">
import '~/components/mm/clips/clips.css';
import ClipsTemplatePreview from '~/components/mm/clips/ClipsTemplatePreview.vue';
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import type { ClipGrowthTemplate } from '~/services/musicService';
import musicService from '~/services/musicService';
import { buildClipTemplateDraft, saveClipRemixDraft } from '~/utils/clipsGrowth';
import { buildClipDraftLocation } from '~/utils/clip-workspace.mjs';
import {
  createGrowthEventId,
  getGrowthEventAnonymousId,
  getGrowthEventSessionId,
} from '~/utils/growthEvents';

const router = useRouter();
const { t } = useI18n();
const templates = ref<ClipGrowthTemplate[]>([]);
const isLoading = ref(true);
const isPreparing = ref(false);
const errorMessage = ref('');
const remixError = ref('');
useHead(() => ({ title: `${t('clips.templates.title')} · MusicMint` }));

const unwrapData = <T,>(payload: any): T => {
  if (typeof payload?.code === 'number' && payload.code >= 400) throw new Error('Templates unavailable');
  return (payload?.data ?? payload) as T;
};

const trackTemplateEvent = (eventType: string, metadata: Record<string, unknown> = {}) => {
  const templateId = metadata.template_id;
  void musicService.trackGrowthEvent({
    event_id: createGrowthEventId(),
    content_type: 'clip_package',
    content_id: typeof templateId === 'string' && templateId ? templateId : 'clips-template',
    event_type: eventType,
    surface: 'clips_templates',
    request_id: createGrowthEventId(),
    anonymous_id: getGrowthEventAnonymousId(),
    session_id: getGrowthEventSessionId(),
    metadata,
  }).catch(() => {});
};

const loadTemplates = async () => {
  if (isLoading.value && templates.value.length) return;
  isLoading.value = true;
  errorMessage.value = '';
  try {
    const response = await musicService.listPublicClipGrowthTemplates();
    const items = unwrapData<ClipGrowthTemplate[]>(response);
    if (!Array.isArray(items)) throw new Error('Invalid templates');
    templates.value = items;
  } catch { errorMessage.value = t('clips.templates.loadFailed'); }
  finally { isLoading.value = false; }
};

const startTemplateRemix = async (template: ClipGrowthTemplate) => {
  if (isPreparing.value) return;
  isPreparing.value = true;
  remixError.value = '';
  try {
    trackTemplateEvent('clips_template_make_yours_click', { template_id: template.id });
    saveClipRemixDraft(buildClipTemplateDraft(template.remix_request, template.id));
    await router.push(buildClipDraftLocation('template', createGrowthEventId(), template.id));
  } catch { remixError.value = t('clips.templates.prepareFailed'); }
  finally { isPreparing.value = false; }
};

onMounted(loadTemplates);
</script>
